"use client";

import {
    Card,
    Form
} from "react-bootstrap";
import {
    useState,
    useEffect
} from "react";
import ReactQuill
    from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css'; // Import Quill style
import {
    useNavigate,
    useLocation
} from 'react-router-dom';
import AvatarUpload
    from "../../../Widgets/AvatarUpload";
import {
    getUserIdFromToken,
    getToken
} from '../../../../utils/auth'; // Adjust path if needed


const quillModules = {
    toolbar: {
        container: [
            [{'header': [1, 2, false]}],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
            ['link', 'image'],
            ['clean']
        ],
        handlers: {
            image: imageHandler
        }
    }
};

// Helper to get Authorization headers
const getAuthHeaders = () => {
    const token = getToken();
    return token ? {'Authorization': `Bearer ${token}`} : {};
};

function imageHandler() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.setAttribute('multiple', 'multiple'); // Enable multiple selection
    input.click();

    input.onchange = async () => {
        const files = Array.from(input.files); // Convert FileList to Array
        if (files.length > 0) {
            const quill = this.quill;
            const range = quill.getSelection();
            let currentIndex = range ? range.index : 0;

            for (const file of files) {
                // 1. Upload image to your API
                const formData = new FormData();
                formData.append('image', file);

                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/topic/upload-image`, {
                        method: 'POST',
                        headers: {
                            ...getAuthHeaders(),
                        },
                        body: formData
                    });
                    
                    const contentType = response.headers.get("content-type");
                    if (!contentType || !contentType.includes("application/json")) {
                        const text = await response.text();
                        console.error('Server error:', text);
                        continue;
                    }
                    
                    const result = await response.json();
                    const imageUrl = result.url;

                    // 2. Insert image by URL directly into Quill
                    quill.insertEmbed(currentIndex, 'image', imageUrl);
                    currentIndex++; // Move cursor after each image
                } catch (error) {
                    console.error('Error uploading image:', error);
                }
            }
        }
    }

}


const BasicSuaTinDang = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        row,
        mode
    } = location.state || {};

    // State for topic categories
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [categoryError, setCategoryError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(
        mode === "edit" ? row?.rowIDTopicCategory || "" : ""
    );

    const [urlMapping, setUrlMapping] = useState(new Map());

    // Utility: Convert image URLs in HTML to base64 and track mapping
    async function convertImagesToBase64(html) {
        const imgTagRegex = /<img[^>]+src=["']([^"'>]+)["'][^>]*>/g;
        let matches = [...html.matchAll(imgTagRegex)];
        let newHtml = html;
        const mapping = new Map();
        for (const match of matches) {
            const url = match[1];
            // Skip if already base64
            if (url.startsWith('data:')) continue;
            try {
                const response = await fetch(url);
                const blob = await response.blob();
                const base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
                mapping.set(base64, url); // Track base64 -> url
                newHtml = newHtml.replace(url, base64);
            } catch (err) {
                console.error('Failed to convert image:', url, err);
            }
        }
        return {html: newHtml, mapping};
    }

    const [formData, setFormData] = useState({
        rowid: row?.rowID || "",
        title: row?.title || "",
        description: row?.description || "",
        avatar: row?.avatar || "",
        slug: row?.slug || "",
        orderNumber: typeof row?.orderNumber === "number" ? row.orderNumber : 0, // always number
        isHighlight: row?.isHighlight || false // checkbox, default false
    });

    const [editorValue, setEditorValue] = useState({
        description: row?.description || ""
    });

    useEffect(() => {
        async function processDescription() {
            if (row?.description) {
                const {html, mapping} = await convertImagesToBase64(row.description);
                setEditorValue({description: html});
                setUrlMapping(mapping);
            } else {
                setEditorValue({description: ""});
                setUrlMapping(new Map());
            }
        }

        processDescription();
    }, [row?.description]);

    // Copilot ignore start
    // Move handleSubmit inside the component to access state

    /*
    * Here’s the clear explanation:
Why is imageHandler outside the component?
imageHandler is referenced by quillModules, which is also defined outside the component.
ReactQuill expects the modules configuration (including handlers) to be a stable object, not recreated on every render.
Defining imageHandler outside ensures it is only created once and is available for the static quillModules object.
imageHandler does not need access to component state or props.
Why must handleSubmit be inside the component?
handleSubmit needs access to the component’s state variables (formData, editorValue) and possibly props.
If handleSubmit is outside, it cannot access these variables, causing "not defined" errors.
Defining handleSubmit inside the component allows it to use the latest state and props via closure.
Summary:
imageHandler is outside because it’s used in a static config and does not need state/props.
handleSubmit is inside because it needs access to state/props.
This is the best practice for React functional components and third-party integrations like ReactQuill.*/

    // Copilot ignore end
    // Utility to extract user ID from JWT stored in localStorage


    const handleSubmit = async (e) => {
        e.preventDefault();

        const createdUserId = getUserIdFromToken();

        // Convert base64 images back to URLs before submitting
        let descriptionForSubmit = editorValue.description;

        // Convert any base64 images back to URLs
        urlMapping.forEach((originalUrl, base64) => {
            descriptionForSubmit = descriptionForSubmit.replace(base64, originalUrl);
        });

        // Also handle any new images that might be URLs already
        const imgTagRegex = /<img[^>]+src=["']([^"'>]+)["'][^>]*>/g;
        const matches = [...descriptionForSubmit.matchAll(imgTagRegex)];

        for (const match of matches) {
            const src = match[1];
            // If it's still a base64 (new image), keep the URL from server response
            if (src.startsWith('data:')) {
                // This should not happen if imageHandler works correctly
                console.warn('Found base64 image in submit:', src.substring(0, 50) + '...');
            }
        }

        const payload = {
            RowID: mode === "edit" ? parseInt(formData.rowid, 10) : undefined,
            Title: formData.title,
            Description: descriptionForSubmit,
            RowIDTopicCategory: selectedCategory,
            Avatar: formData.avatar.replace(process.env.REACT_APP_API_IMAGE_URL, ''), // Remove base URL
            CreatedUserID: createdUserId,
            Slug: formData.slug,
            OrderNumber: formData.orderNumber, // add to payload
            IsHighlight: formData.isHighlight // add to payload
        };

        try {
            let url = `${process.env.REACT_APP_API_BASE_URL}/topic/insert-topic`;
            if (mode === "edit") {
                url = `${process.env.REACT_APP_API_BASE_URL}/topic/update-topic`;
            }
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                alert('Topic updated successfully!');
                setFormData({
                    rowid: "",
                    title: "",
                    description: "",
                    avatar: "",
                    slug: "",
                    orderNumber: 0,
                    isHighlight: false
                }); // reset orderNumber to 0
                setEditorValue({description: ""});
                setSelectedCategory("");
            } else {
                const error = await response.text();
                alert('Error: ' + error);
            }
        } catch (err) {
            alert('Network error: ' + err.message);
        }
    }

    // Fetch topic categories on mount
    useEffect(() => {
        async function fetchCategories() {
            setLoadingCategories(true);
            setCategoryError(null);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/topic/get-topic-categories`, {
                    headers: {
                        ...getAuthHeaders(),
                    },
                });
                if (!response.ok) throw new Error("Failed to fetch categories");
                
                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    const text = await response.text();
                    throw new Error("Server error: " + text);
                }
                
                const data = await response.json();
                setCategories(data);
                // If editing, set selectedCategory from row
                if (mode === "edit" && row?.rowIDTopicCategory) {
                    setSelectedCategory(row.rowIDTopicCategory);
                }
            } catch (err) {
                setCategoryError(err.message);
            } finally {
                setLoadingCategories(false);
            }
        }

        fetchCategories();
    }, [mode, row?.rowIDTopicCategory]);

    // Ensure formData.avatar updates when row?.avatar changes (edit mode)
    useEffect(() => {
        console.log('Edit mode useEffect triggered:', {mode, rowAvatar: row?.avatar, rowID: row?.rowID});

        if (mode === "edit" && row?.avatar) {
            console.log('Setting avatar for edit mode:', row.avatar);
            // Keep avatar as URL for edit mode - no need to convert to base64
            // The AvatarUpload component can handle URLs directly
            setFormData(prev => ({
                ...prev,
                avatar: row.avatar ? `${process.env.REACT_APP_API_IMAGE_URL}${row.avatar}` : ""
            }));
        } else if (mode === "edit") {
            console.log('Edit mode but no avatar found in row data');
        }
    }, [mode, row?.avatar, row?.rowID]);

    // Debug log when formData.avatar changes
    useEffect(() => {
        console.log('formData.avatar changed to:', formData.avatar);
    }, [formData.avatar]);

    // Handle new avatar uploads - similar to Quill.js imageHandler
    const handleAvatarUpload = async (url) => {
        // When avatar is uploaded (similar to imageHandler), store the URL directly
        // This ensures we always have the latest uploaded URL from the server
        setFormData(prevFormData => ({
            ...prevFormData,
            avatar: url
        }));


        console.log('Avatar uploaded successfully:', url);
    };

    // Sync formData with row data on mode or row change
    useEffect(() => {
        if (mode === "edit") {
            setFormData(prev => ({
                ...prev,
                orderNumber: row?.orderNumber || 0,
                isHighlight: row?.isHighlight || false
            }));
        }
    }, [mode, row?.orderNumber, row?.isHighlight]);

    return (
        <>
            <Card
                className="bg-white border-0 rofunded-3 mb-4">
                <Card.Body
                    className="p-4">
                    <h4 className="fs-18 mb-4">Basic
                        Form</h4>
                    <Form
                        onSubmit={handleSubmit}>
                        {mode === "edit" && (
                            <Form.Group
                                className="mb-4">
                                <label
                                    className="label text-secondary">RowID</label>
                                <Form.Group>
                                    <Form.Control
                                        type="text"
                                        className="text-dark h-55"
                                        placeholder="Enter Name"
                                        value={formData.rowid}
                                        readOnly={true}
                                    />
                                </Form.Group>
                            </Form.Group>)}

                        <Form.Group
                            className="mb-4">
                            <label
                                className="label text-secondary">Danh
                                mục</label>
                            {loadingCategories ? (
                                <div>Tải
                                    danh
                                    mục...</div>
                            ) : categoryError ? (
                                <div
                                    className="text-danger">{categoryError}</div>
                            ) : (
                                <Form.Select
                                    value={selectedCategory}
                                    onChange={e => setSelectedCategory(e.target.value)}
                                    required
                                >
                                    <option
                                        value="">Loại
                                        tin
                                        đăng
                                    </option>
                                    {categories.filter(c => !c.isDeleted).map(cat => (
                                        <option
                                            key={cat.rowID}
                                            value={cat.rowID}>{cat.name}</option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>

                        <Form.Group
                            className="mb-4">
                            <label
                                className="label text-secondary">Title</label>
                            <Form.Group>
                                <Form.Control
                                    type="text"
                                    className="text-dark h-55"
                                    placeholder="Enter Name"
                                    value={formData.title}
                                    onChange={e => setFormData({
                                        ...formData,
                                        title: e.target.value
                                    })}
                                />
                            </Form.Group>
                        </Form.Group>

                        <Form.Group
                            className="mb-4">
                            <label
                                className="label text-secondary">Mô
                                tả</label>
                            <Form.Group>
                                {/*<Form.Control*/}
                                {/*    type="text"*/}
                                {/*    className="text-dark h-55"*/}
                                {/*    placeholder="Enter Name"*/}
                                {/*    value={formData.description}*/}
                                {/*    onChange={e => setFormData({...formData, description: e.target.value})}*/}
                                {/*/>*/}
                                <ReactQuill
                                    value={editorValue.description}
                                    onChange={e => setEditorValue({
                                        ...editorValue,
                                        description: e
                                    })}
                                    theme="snow"
                                    modules={quillModules}
                                />
                            </Form.Group>


                        </Form.Group>

                        <Form.Group
                            className="mb-4">
                            <label
                                className="label text-secondary">Avatar</label>
                            <AvatarUpload
                                initialAvatar={formData.avatar}
                                onAvatarUploaded={handleAvatarUpload}
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <label className="label text-secondary">Thứ tự tin HOT (0 là bỏ)</label>
                            <Form.Control
                                type="number"
                                min={0}
                                value={formData.orderNumber ?? 0} // always number
                                onChange={e => setFormData({
                                    ...formData,
                                    orderNumber: Math.max(0, parseInt(e.target.value, 10) || 0)
                                })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Check
                                type="checkbox"
                                label="Tô đậm tin HOT"
                                checked={formData.isHighlight}
                                onChange={e => setFormData({
                                    ...formData,
                                    isHighlight: e.target.checked
                                })}
                            />
                        </Form.Group>

                        <div
                            className="d-flex gap-2">
                            <button
                                type="submit"
                                className="btn btn-primary bg-primary bg-opacity-10 text-primary border-0 fw-semibold py-2 px-4"
                            >
                                {mode === "edit" ? 'Cập nhật' : 'Đăng tin'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger bg-danger bg-opacity-10 text-danger border-0 fw-semibold py-2 px-4"
                                onClick={() => navigate('/tuvien/tindang')}
                            >
                                Hủy
                            </button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </>
    );
};

export default BasicSuaTinDang;
